import { cn } from "@/lib/utils";

interface NetflixBookSkeletonProps {
  className?: string;
  /** Stagger delay in ms for wave effect */
  delay?: number;
}

/**
 * Netflix-style shimmer skeleton for book cards.
 * Single cover-only shimmer - matches the exact book card shape.
 * This is the ONLY skeleton shown during loading - no nested shimmers.
 */
export function NetflixBookSkeleton({ className, delay = 0 }: NetflixBookSkeletonProps) {
  return (
    <div 
      className={cn(
        "aspect-[2/3] relative overflow-hidden rounded-[8px] bg-white/5",
        className
      )}
    >
      {/* Single shimmer sweep - Netflix style */}
      <div 
        className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite]"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
          animationDelay: `${delay}ms`,
        }}
      />
    </div>
  );
}

/**
 * Netflix-style shimmer skeleton for cinema carousel cards (landscape)
 */
export function NetflixCinemaCardSkeleton({ className }: { className?: string }) {
  return (
    <div 
      className={cn(
        "relative overflow-hidden rounded-3xl bg-white/5 aspect-video",
        className
      )}
    >
      {/* Shimmer effect */}
      <div 
        className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite]"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
        }}
      />
    </div>
  );
}

/**
 * Netflix-style row skeleton with header placeholder
 */
export function NetflixRowSkeleton({ 
  cardCount = 6,
  showTitle = true,
  variant = 'portrait'
}: { 
  cardCount?: number;
  showTitle?: boolean;
  variant?: 'portrait' | 'landscape';
}) {
  return (
    <div className="space-y-3">
      {showTitle && (
        <div className="px-4 md:px-12 xl:px-16">
          <div className="h-6 w-40 rounded bg-white/5 overflow-hidden relative">
            <div 
              className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite]"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
              }}
            />
          </div>
        </div>
      )}
      <div className="flex gap-3 px-4 md:px-12 xl:px-16 overflow-hidden">
        {Array.from({ length: cardCount }).map((_, i) => (
          <div 
            key={i} 
            className={cn(
              "flex-shrink-0",
              variant === 'portrait' 
                ? "w-[120px] sm:w-[140px] md:w-[160px]"
                : "w-[208px] sm:w-[260px] md:w-[312px]"
            )}
          >
            {variant === 'landscape' ? (
              <NetflixCinemaCardSkeleton />
            ) : (
              <NetflixBookSkeleton delay={i * 100} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
